import { useState } from "react";
import { Check } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { Field, TextInput, SelectInput, TextArea, FormNotice } from "@/components/FormControls";
import api, { formatApiErrorDetail } from "@/lib/api";
import { supportingMothers } from "@/data/content";
import { useBrand } from "@/context/BrandContext";
import media from "@/config/media";

const emptyForm = {
  name: "",
  mobile: "",
  email: "",
  location: "",
  experience: "",
  availability: "",
  current_employment: "",
  opportunity_type: "",
  message: "",
};

const OpportunityForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [state, setState] = useState({ status: "idle", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/leads/cook-application", form);
      setState({ status: "done", message: data.message });
      setForm(emptyForm);
    } catch (err) {
      setState({
        status: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <form onSubmit={submit} className="mbm-card p-6 sm:p-8" data-testid="opportunity-form">
      <h3 className="font-heading text-lg font-semibold">Register your interest</h3>
      <p className="mt-2 text-sm text-cocoa/80">
        The programme is in development. Leave your details and we will contact you as roles open.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="Name" required>
          <TextInput required value={form.name} onChange={set("name")} data-testid="op-name" />
        </Field>
        <Field label="Mobile" required>
          <TextInput required type="tel" value={form.mobile} onChange={set("mobile")} data-testid="op-mobile" />
        </Field>
        <Field label="Email" required className="sm:col-span-2">
          <TextInput required type="email" value={form.email} onChange={set("email")} data-testid="op-email" />
        </Field>
        <Field label="Location">
          <TextInput value={form.location} onChange={set("location")} data-testid="op-location" placeholder="Area in Pune" />
        </Field>
        <Field label="Cooking experience">
          <SelectInput value={form.experience} onChange={set("experience")} data-testid="op-experience">
            <option value="">Select</option>
            <option value="home">Home cooking only</option>
            <option value="1-3">1–3 years professional</option>
            <option value="3-plus">More than 3 years professional</option>
            <option value="trained">Formally trained</option>
          </SelectInput>
        </Field>
        <Field label="Availability">
          <SelectInput value={form.availability} onChange={set("availability")} data-testid="op-availability">
            <option value="">Select</option>
            <option value="mornings">Mornings</option>
            <option value="full-day">Full day</option>
            <option value="part-time">Part time</option>
            <option value="flexible">Flexible</option>
          </SelectInput>
        </Field>
        <Field label="Current employment">
          <TextInput value={form.current_employment} onChange={set("current_employment")} data-testid="op-employment" />
        </Field>
        <Field label="Type of opportunity sought" className="sm:col-span-2">
          <SelectInput value={form.opportunity_type} onChange={set("opportunity_type")} data-testid="op-type">
            <option value="">Select</option>
            <option value="cook">Cook</option>
            <option value="kitchen-assistant">Kitchen assistant</option>
            <option value="packing">Packing and quality checks</option>
            <option value="kitchen-partner">Running a partner kitchen</option>
            <option value="other">Something else</option>
          </SelectInput>
        </Field>
        <Field label="Short message" className="sm:col-span-2">
          <TextArea value={form.message} onChange={set("message")} data-testid="op-message" rows={3} />
        </Field>
      </div>
      {state.message && (
        <div className="mt-5">
          <FormNotice tone={state.status === "error" ? "error" : "info"} testId="opportunity-message">
            {state.message}
          </FormNotice>
        </div>
      )}
      <CTAButton
        type="submit"
        variant="secondary"
        className="mt-6 w-full"
        disabled={state.status === "loading"}
        data-testid="opportunity-submit"
      >
        {state.status === "loading" ? "Sending…" : "Register interest"}
      </CTAButton>
    </form>
  );
};

export default function SupportingMothersPage() {
  const brand = useBrand();
  return (
    <>
      <PageHero
        eyebrow="Mothers supporting mothers"
        title={supportingMothers.headline}
        intro={supportingMothers.intro}
       
      >
        <span
          data-testid="programme-status-label"
          className="inline-block rounded-full bg-amber px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.12em] text-cocoa"
        >
          {brand.programme.statusLabel}
        </span>
      </PageHero>

      <Section id="why" eyebrow="Why it matters" title="Two kinds of support, one business.">
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {supportingMothers.why.map((w, i) => (
            <Reveal key={w} delay={i * 90}>
              <div className="mbm-card h-full p-6">
                <span className="font-heading text-2xl font-semibold text-coralInk/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 text-sm leading-relaxed text-cocoa/70">{w}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section
        id="standards"
        tone="creamDeep"
        eyebrow="Standards before expansion"
        title="Anyone cooking for a child works to the same written standards."
        intro={supportingMothers.standardsNote}
      >
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-start">
          <ul className="grid gap-3 sm:grid-cols-2">
            {supportingMothers.standards.map((s, i) => (
              <Reveal as="li" key={s} delay={i * 50}>
                <p className="flex items-center gap-2.5 rounded-control bg-surface shadow-soft px-4 py-3.5 text-sm font-medium text-cocoa">
                  <Check className="h-4 w-4 shrink-0 text-coralInk" aria-hidden="true" />
                  {s}
                </p>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={120}>
            <div className="overflow-hidden rounded-[32px] shadow-softLg">
              <img
                src={media.kitchenPrep}
                alt="Two women cooks preparing vegetables in a clean kitchen"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section id="opportunity" tone="sage">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <h2 className="font-heading text-2xl font-semibold sm:text-3xl">
              If you cook well, we would like to know you.
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-cocoa/70 sm:text-base">
              As the kitchen network grows we will need trained cooks, kitchen assistants and
              partners who can hold a standard. Roles are not open yet — this list is how we will
              start the conversation when they are.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <OpportunityForm />
          </Reveal>
        </div>
      </Section>
    </>
  );
}
